// src/pages/system/ServiceBoard.jsx
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { requireUser } from '../../context/Sessions';
import svcStyles from './Service.module.css';

/* ----------------------- loader ----------------------- */
export async function loader({ request }) {
    const { role, tenant_id } = await requireUser(request);
    if (role !== 'PERSONAL' && role !== 'USER') {
        throw new Response('Forbidden', { status: 403 });
    }

    const base = tenant_id ? `/api/service/model_list/${tenant_id}` : `/api/service/model_list`;

    const res = await fetch(base, {
        headers: { Accept: 'application/json' },
        credentials: 'include',
    });
    if (!res.ok) throw new Response('Failed to load my services', { status: res.status });

    const data = await res.json();

    const services = Array.isArray(data)
        ? data.map((item, i) => {
              const id = String(item.key ?? item.id ?? item.service_id ?? `svc-${i + 1}`);
              const image = item.imageUrl ? `/uploads/${encodeURIComponent(item.imageUrl)}` : null;

              // 층 정보: 백엔드 구조에 맞게 적당히 추출
              let floorsText = '-';
              if (Array.isArray(item.floors) && item.floors.length > 0) {
                  floorsText = item.floors.join(', ');
              } else if (item.floorRange) {
                  floorsText = item.floorRange; // 예: "B2~16F"
              }

              return {
                  id,
                  name: item.name ?? `모델 ${i + 1}`,
                  image,
                  floors: floorsText,
                  lastUsedAt: item.lastUsedAt,
                  raw: item,
              };
          })
        : [];

    return { services, role };
}

/* 최근 사용 시각 포맷터 */
function formatRecent(ts) {
    if (!ts) return '-';
    try {
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return '-';
        // 한국 기준 표기
        return new Intl.DateTimeFormat('ko-KR', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).format(d);
    } catch {
        return '-';
    }
}

/* --------------------- component ---------------------- */
export default function ServiceBoard() {
    const { services, role } = useLoaderData();
    const nav = useNavigate();

    const [query, setQuery] = useState('');
    const [preview, setPreview] = useState(null); // { url, name }

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return services;
        return services.filter((s) => (s.name ?? '').toLowerCase().includes(q));
    }, [services, query]);

    const handleRun = (svc) => {
        nav(`/simulation?modelId=${encodeURIComponent(svc.id)}`);
    };

    const handleThumbClick = (svc) => {
        if (!svc.image) return;
        setPreview({ url: svc.image, name: svc.name });
    };

    return (
        <main className={svcStyles.wrap}>
            {/* 헤더 */}
            <header className={svcStyles.pageHeader}>
                <h1 className={svcStyles.pageTitle}>시뮬레이션 모델 선택</h1>
            </header>

            {/* 검색 + 카운트 영역 */}
            <div className={svcStyles.boardTopBar}>
                <div className={svcStyles.boardSearch}>
                    <label className={svcStyles.boardSearchLabel}>모델 이름 검색</label>
                    <input type="text" className={svcStyles.boardSearchInput} placeholder="예: A타워, 3동 강의동 등" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
                <div className={svcStyles.boardCount}>
                    전체 <b>{services.length}</b>개
                    {query.trim() && (
                        <>
                            <span className={svcStyles.boardCountDivider}>·</span>
                            검색 결과 <b>{filtered.length}</b>개
                        </>
                    )}
                </div>
            </div>

            {/* 테이블 영역 */}
            <section className={svcStyles.boardTableSection}>
                {filtered.length === 0 ? (
                    <div className={svcStyles.boardEmpty}>
                        {services.length === 0 ? (
                            role === 'USER' ? (
                                <>
                                    신청한 서비스(모델)가 없습니다. <br />
                                    조직 관리자에게 모델 사용 신청을 요청하세요.
                                </>
                            ) : (
                                <>
                                    신청한 서비스(모델)가 없습니다. <br />
                                    <a className="u-link" href="/services-apply">
                                        서비스 신청 페이지
                                    </a>
                                    에서 먼저 모델을 신청해주세요.
                                </>
                            )
                        ) : (
                            <>검색 조건에 해당하는 모델이 없습니다.</>
                        )}
                    </div>
                ) : (
                    <div className={svcStyles.boardTableWrap}>
                        {/* 헤더 행 */}
                        <div className={`${svcStyles.boardRow} ${svcStyles.boardRowHead}`}>
                            <div className={svcStyles.boardCellName}>모델</div>
                            <div className={svcStyles.boardCellRecent}>최근 사용</div>
                            <div className={svcStyles.boardCellAction}>실행</div>
                        </div>

                        {/* 데이터 행 */}
                        {filtered.map((svc) => (
                            <div key={svc.id} className={svcStyles.boardRow}>
                                {/* 모델(이미지 + 이름/ID) */}
                                <div className={svcStyles.boardCellName}>
                                    <div className={svcStyles.admServiceCell}>
                                        {svc.image ? (
                                            <button type="button" className={svcStyles.boardThumbBtn} onClick={() => handleThumbClick(svc)} title="이미지 확대">
                                                <img src={svc.image} alt="" className={svcStyles.boardThumb} />
                                            </button>
                                        ) : (
                                            <div className={svcStyles.boardThumbPlaceholder}>{svc.name?.slice(0, 2) ?? '모델'}</div>
                                        )}

                                        <div className={svcStyles.admServiceText}>
                                            <div className={svcStyles.admServiceName}>{svc.name}</div>
                                            <div className={svcStyles.admServiceMeta}>ID: {svc.id}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* 최근 사용 */}
                                <div className={svcStyles.boardCellRecent}>
                                    <span className={svcStyles.boardRecentText}>{formatRecent(svc.lastUsedAt)}</span>
                                </div>

                                {/* 실행 버튼 */}
                                <div className={svcStyles.boardCellAction}>
                                    <button type="button" className={`${svcStyles.btn} ${svcStyles.btnPrimary}`} onClick={() => handleRun(svc)}>
                                        실행
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* 이미지 확대 팝업 */}
            {preview && (
                <div className={svcStyles.boardImgOverlay} onClick={() => setPreview(null)}>
                    <div className={svcStyles.boardImgModal} onClick={(e) => e.stopPropagation()}>
                        <div className={svcStyles.boardImgModalHeader}>
                            <div className={svcStyles.boardImgTitle}>{preview.name}</div>
                            <button type="button" className={svcStyles.boardImgClose} onClick={() => setPreview(null)}>
                                ✕
                            </button>
                        </div>
                        <div className={svcStyles.boardImgBody}>
                            <img src={preview.url} alt={preview.name} className={svcStyles.boardImgLarge} />
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
